/**
 * PaginationBar (KAN-731)
 *
 * Общая панель под списками: "Показывать по" 10/20/50/100, "Страница X из Y",
 * Назад/Вперёд. Состояние (страница, размер) живёт у вызывающего, обычно в
 * хуке usePageSize. Сохраняет data-testid прежней пагинации (pagination,
 * pagination-info, pagination-prev, pagination-next).
 * Список не длиннее самого маленького размера (10) панель не показывает:
 * ни листать, ни выбирать нечего.
 */
import { useId } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { NativeSelect } from '@/components/ui/native-select';
import { cn } from '@/lib/utils';
import { PAGE_SIZE_OPTIONS } from '@/hooks/usePageSize';

export interface PaginationBarProps {
  page: number;
  pageSize: number;
  /** Всего записей в списке (count из ответа API или длина локального списка). */
  count: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  /** Флаги сервера (next/previous); без них границы считаются по count. */
  hasNext?: boolean;
  hasPrevious?: boolean;
  className?: string;
}

const SIZE_OPTIONS = PAGE_SIZE_OPTIONS.map((size) => ({
  value: String(size),
  label: String(size),
}));

export function PaginationBar({
  page,
  pageSize,
  count,
  onPageChange,
  onPageSizeChange,
  hasNext,
  hasPrevious,
  className,
}: PaginationBarProps): JSX.Element | null {
  const selectId = useId();
  const totalPages = Math.max(1, Math.ceil(count / pageSize));
  const canPrev = hasPrevious ?? page > 1;
  const canNext = hasNext ?? page < totalPages;

  if (count <= PAGE_SIZE_OPTIONS[0]) return null;

  return (
    <div
      data-testid="pagination"
      className={cn('flex flex-wrap items-center justify-between gap-3 mt-4 px-4', className)}
    >
      <div className="flex items-center gap-3 text-sm text-gray-500">
        <label htmlFor={selectId} className="flex items-center gap-2">
          <span>Показывать по</span>
          <NativeSelect
            id={selectId}
            data-testid="page-size-select"
            className="h-8 w-[76px]"
            options={SIZE_OPTIONS}
            value={String(pageSize)}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
          />
        </label>
        <span data-testid="pagination-info">
          Страница {page} из {totalPages}
        </span>
      </div>
      <div className="flex items-center gap-2">
        <Button
          data-testid="pagination-prev"
          variant="outline"
          size="sm"
          onClick={() => onPageChange(Math.max(1, page - 1))}
          disabled={!canPrev}
        >
          <ChevronLeft className="h-4 w-4 sm:mr-1" />
          <span className="hidden sm:inline">Назад</span>
        </Button>
        <Button
          data-testid="pagination-next"
          variant="outline"
          size="sm"
          onClick={() => onPageChange(page + 1)}
          disabled={!canNext}
        >
          <span className="hidden sm:inline">Вперёд</span>
          <ChevronRight className="h-4 w-4 sm:ml-1" />
        </Button>
      </div>
    </div>
  );
}
